import { useFormikContext } from 'formik';
import React, { useEffect } from 'react';

import InfoIcon from '@mui/icons-material/Info';
import { Grid, Tooltip, InputAdornment, Typography } from '@mui/material';

import { Job } from '@libs/graphql-types';
import { modelPath } from '@libs/helpers/form';
import { ConnectedTextField } from '@libs/ui/components/form/text-field';

import { generateBayerSecurityId } from '../../security-id-utils';
import { ApplicationFormState } from '../../types';
import { BirthDateFields } from './BirthDateFields';

interface BayerSocialSecurityIdProps {
  job: Job;
  showOnlyRequired?: boolean;
}

/**
 * Hook to automatically sync the Bayer security ID field when separate components change
 */
export const useBayerSecurityIdSync = () => {
  const { values, setFieldValue } = useFormikContext<ApplicationFormState>();

  useEffect(() => {
    const { firstName, lastName } = values.basicInfo;
    const { monthOfBirth, dayOfBirth } = values.details;

    // Only generate if all required fields are present
    if (firstName && lastName && monthOfBirth > 0 && dayOfBirth > 0) {
      const securityId = generateBayerSecurityId(
        firstName,
        lastName,
        monthOfBirth,
        dayOfBirth,
      );

      // Update both security ID and confirm security ID fields
      if (securityId !== values.details.securityId) {
        setFieldValue('details.securityId', securityId);
        setFieldValue('details.confirmSecurityId', securityId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.basicInfo.firstName,
    values.basicInfo.lastName,
    values.details.monthOfBirth,
    values.details.dayOfBirth,
    values.details.securityId,
    setFieldValue,
  ]);
};

/**
 * Bayer-specific Social Security ID form component
 * This component renders a custom form for Bayer job applications
 */
export const BayerSocialSecurityId: React.FC<BayerSocialSecurityIdProps> = ({
  job,
  showOnlyRequired = false,
}) => {
  useBayerSecurityIdSync();
  return (
    <>
      <Grid item>
        <Typography variant="h6" gutterBottom>
          Security ID Information{' '}
          <Typography component="span" color="error">
            *
          </Typography>
        </Typography>
      </Grid>

      {/* Birth date fields - reused component */}
      <Grid item>
        <BirthDateFields showOnlyRequired={showOnlyRequired} />
      </Grid>

      <Grid item>
        <ConnectedTextField
          name={modelPath<ApplicationFormState>((i) => i.details.securityId)}
          fullWidth
          variant="filled"
          label="Security ID"
          disabled
          placeholder="FFFMMDDNNNN"
          inputProps={{ maxLength: 11 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Security ID">
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
    </>
  );
};
